import React, { useState, useEffect } from 'react';
import api from '../api/axiosInstance';
import { ArrowDown, ArrowUp, ArrowsClockwise, Minus, ArrowRight } from '@phosphor-icons/react';
import { Card } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
}).format(v || 0);

const fmtCompact = (v) => {
  if (!v) return '$0';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)    return `$${(v / 1_000).toFixed(0)}K`;
  return `$${Math.round(v)}`;
};

// Color scale for matrix cells based on |value| relative to max
const cellStyle = (net, maxAbs) => {
  if (net === 0 || maxAbs === 0) return { bg: 'bg-gray-50', text: 'text-gray-400' };
  const intensity = Math.min(1, Math.abs(net) / maxAbs);
  if (net > 0) {
    if (intensity > 0.66) return { bg: 'bg-red-100',  text: 'text-red-700' };
    if (intensity > 0.33) return { bg: 'bg-red-50',   text: 'text-red-600' };
    return                       { bg: 'bg-orange-50', text: 'text-orange-500' };
  } else {
    if (intensity > 0.66) return { bg: 'bg-emerald-100', text: 'text-emerald-700' };
    if (intensity > 0.33) return { bg: 'bg-emerald-50',  text: 'text-emerald-600' };
    return                       { bg: 'bg-teal-50',     text: 'text-teal-500' };
  }
};

export const NettingPage = () => {
  const [bankPositions, setBankPositions] = useState([]);
  const [nettingResults, setNettingResults] = useState([]);
  const [bilateral, setBilateral] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 5000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [bpRes, nrRes, bmRes] = await Promise.allSettled([
        api.get(`${API}/netting/bank-positions`),
        api.get(`${API}/netting-results`),
        api.get(`${API}/netting/bilateral-matrix`),
      ]);
      if (bpRes.status === 'fulfilled') setBankPositions(bpRes.value.data);
      if (nrRes.status === 'fulfilled') setNettingResults(nrRes.value.data);
      if (bmRes.status === 'fulfilled') setBilateral(bmRes.value.data);
    } catch (e) { console.error('Fetch data error:', e); }
  };

  const totalInitial = bankPositions.reduce((s, b) => s + b.initial_outbound_count, 0);
  const totalFinal   = bankPositions.reduce((s, b) => s + b.final_outbound_count, 0);
  const nettedCount  = totalInitial - totalFinal;
  const efficiency   = totalInitial > 0 ? Math.round((nettedCount / totalInitial) * 100) : 0;

  const maxAbs = bilateral?.banks?.length
    ? Math.max(...bilateral.banks.flatMap(b1 =>
        bilateral.banks.map(b2 => b1 !== b2 ? Math.abs(bilateral.matrix[b1]?.[b2] || 0) : 0)
      ))
    : 0;

  return (
    <div className="space-y-4 fade-in">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black tracking-tight text-gray-900">Netting</h1>
          <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide font-medium">
            Bank positions &amp; bilateral netting matrix
          </p>
        </div>
        <span className="text-xs font-mono text-gray-400 bg-gray-100 px-3 py-1.5 rounded-full">
          {nettingResults.length} cycle{nettingResults.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Initial',        value: totalInitial,     color: 'text-gray-900' },
          { label: 'Post Netting',   value: totalFinal,       color: 'text-indigo-600' },
          { label: 'Netted',         value: nettedCount,      color: 'text-emerald-600' },
          { label: 'Efficiency',     value: `${efficiency}%`, color: efficiency >= 50 ? 'text-emerald-600' : 'text-amber-600' },
        ].map(({ label, value, color }) => (
          <Card key={label} className="p-3 bg-white border border-gray-200 rounded-lg overflow-hidden">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">{label}</p>
            <p className={`text-sm font-black font-mono truncate ${color}`}>{value}</p>
          </Card>
        ))}
      </div>

      {/* ═══════════════════════════════════════
          BANK NET POSITIONS (moved to top)
          ═══════════════════════════════════════ */}
      <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="bank-positions-table">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div>
            <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Bank Net Positions</h2>
            <p className="text-xs text-gray-500 mt-0.5">Per-bank initial queue counts vs final (post-netting) positions</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1"><ArrowUp size={10} weight="bold" className="text-red-500" /> Out</span>
            <span className="flex items-center gap-1"><ArrowDown size={10} weight="bold" className="text-emerald-500" /> In</span>
          </div>
        </div>

        {bankPositions.length === 0 ? (
          <div className="py-10 text-center">
            <ArrowsClockwise size={28} weight="duotone" className="text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No bank data yet</p>
            <p className="text-xs text-gray-400 mt-1">Initialize the simulation to populate bank positions</p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="grid grid-cols-[6rem_1fr_1fr_1fr] gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wide font-semibold">
              <span>Bank</span>
              <span className="text-center border-l border-gray-200 pl-2">Initial</span>
              <span className="text-center border-l border-gray-200 pl-2">Post Net</span>
              <span className="text-right border-l border-gray-200 pl-2">Net Pos.</span>
            </div>
            <div className="divide-y divide-gray-100">
              {bankPositions.map((b) => {
                const netPositive = b.final_net_amount >= 0;
                return (
                  <div key={b.bank} className="grid grid-cols-[6rem_1fr_1fr_1fr] gap-2 px-4 py-2.5 hover:bg-gray-50 transition-colors items-center" data-testid={`bank-row-${b.bank}`}>
                    <span className="text-xs font-black font-mono text-gray-800">{b.bank}</span>
                    <div className="border-l border-gray-200 pl-2 space-y-0.5">
                      <div className="flex items-center gap-1">
                        <ArrowUp size={9} weight="bold" className="text-red-400 flex-shrink-0" />
                        <span className="text-xs font-mono text-gray-600">{b.initial_outbound_count}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ArrowDown size={9} weight="bold" className="text-emerald-500 flex-shrink-0" />
                        <span className="text-xs font-mono text-gray-600">{b.initial_inbound_count}</span>
                      </div>
                    </div>
                    <div className="border-l border-gray-200 pl-2 space-y-0.5">
                      <div className="flex items-center gap-1">
                        <ArrowUp size={9} weight="bold" className="text-red-400 flex-shrink-0" />
                        <span className="text-xs font-mono text-gray-600">{b.final_outbound_count}</span>
                        {b.initial_outbound_count > b.final_outbound_count && (
                          <span className="text-xs font-mono text-emerald-600">−{b.initial_outbound_count - b.final_outbound_count}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <ArrowDown size={9} weight="bold" className="text-emerald-500 flex-shrink-0" />
                        <span className="text-xs font-mono text-gray-600">{b.final_inbound_count}</span>
                      </div>
                    </div>
                    <div className="border-l border-gray-200 pl-2 text-right">
                      {b.final_net_amount === 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs font-black font-mono text-gray-500"><Minus size={9} weight="bold" /> FLAT</span>
                      ) : (
                        <>
                          <span className={`text-xs font-black font-mono ${netPositive ? 'text-red-600' : 'text-emerald-600'}`}>
                            {netPositive ? '▲' : '▼'} {fmtCompact(Math.abs(b.final_net_amount))}
                          </span>
                          <p className="text-xs text-gray-400 font-mono mt-0.5">{netPositive ? 'payer' : 'receiver'}</p>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* ═══════════════════════════════════════
          BILATERAL NETTING MATRIX
          ═══════════════════════════════════════ */}
      <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="bilateral-matrix-card">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Bilateral Netting Matrix</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Row bank's net obligation to column — <span className="text-red-600 font-semibold">red = payer</span>, <span className="text-emerald-600 font-semibold">green = receiver</span>
            </p>
          </div>
          {bilateral?.total_gross_flows > 0 && (
            <div className="flex-shrink-0 text-right">
              <p className="text-xs text-gray-400 font-mono">Gross: <span className="text-gray-700 font-bold">{fmtCompact(bilateral.total_gross_flows)}</span></p>
              <p className="text-xs text-gray-400 font-mono">Net req: <span className="text-gray-700 font-bold">{fmtCompact(bilateral.total_net_required)}</span></p>
              <p className="text-xs text-emerald-600 font-mono font-bold">Saved {bilateral.savings_pct}%</p>
            </div>
          )}
        </div>

        {!bilateral?.banks?.length ? (
          <div className="py-12 text-center">
            <ArrowsClockwise size={30} weight="duotone" className="text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No data yet — initialize the simulation to populate the matrix</p>
          </div>
        ) : (
          <div className="p-4 overflow-auto">
            {/* Matrix grid */}
            <div className="inline-block min-w-full">
              {/* Column header row */}
              <div className="flex items-center mb-1">
                <div className="w-16 flex-shrink-0 text-xs text-gray-400 font-semibold pr-2 text-right">
                  Payer ↓<br/>Rcvr →
                </div>
                {bilateral.banks.map(colBank => (
                  <div key={colBank} className="w-16 flex-shrink-0 text-center text-xs font-black font-mono text-gray-700 py-1">
                    {colBank.replace('BANK-', '')}
                  </div>
                ))}
              </div>

              {/* Data rows */}
              {bilateral.banks.map(rowBank => (
                <div key={rowBank} className="flex items-center mb-0.5">
                  <div className="w-16 flex-shrink-0 text-xs font-black font-mono text-gray-700 pr-2 text-right">
                    {rowBank.replace('BANK-', '')}
                  </div>

                  {bilateral.banks.map(colBank => {
                    const isDiag = rowBank === colBank;
                    const net    = isDiag ? 0 : (bilateral.matrix[rowBank]?.[colBank] || 0);
                    const style  = isDiag ? { bg: 'bg-gray-100', text: 'text-gray-300' } : cellStyle(net, maxAbs);

                    return (
                      <div
                        key={colBank}
                        className={`w-16 h-9 flex-shrink-0 mx-0.5 rounded flex flex-col items-center justify-center cursor-default relative ${style.bg}`}
                        data-testid={`matrix-${rowBank}-${colBank}`}
                        onMouseEnter={() => !isDiag && setTooltip({ rowBank, colBank, net })}
                        onMouseLeave={() => setTooltip(null)}
                      >
                        {isDiag ? (
                          <Minus size={10} className="text-gray-300" weight="bold" />
                        ) : (
                          <>
                            <span className={`text-[10px] font-black font-mono leading-none ${style.text}`}>
                              {fmtCompact(Math.abs(net))}
                            </span>
                            <span className={`text-[8px] font-semibold mt-0.5 ${style.text} opacity-75`}>
                              {net > 0 ? '▲ payer' : net < 0 ? '▼ rcvr' : 'flat'}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Tooltip */}
            {tooltip && (
              <div className="mt-3 p-3 bg-gray-900 text-white rounded-lg text-xs font-mono max-w-xs">
                <p className="font-black mb-1">{tooltip.rowBank} → {tooltip.colBank}</p>
                <p>Net obligation: <span className={tooltip.net > 0 ? 'text-red-300' : 'text-emerald-300'}>{fmt(tooltip.net)}</span></p>
                <p className="text-gray-400 mt-1">{tooltip.net > 0 ? `${tooltip.rowBank} is net payer` : tooltip.net < 0 ? `${tooltip.rowBank} is net receiver` : 'Flat position'}</p>
              </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">Legend:</span>
              {[
                { bg: 'bg-red-100',      text: 'text-red-700',      label: 'High payer' },
                { bg: 'bg-orange-50',    text: 'text-orange-500',   label: 'Low payer' },
                { bg: 'bg-emerald-100',  text: 'text-emerald-700',  label: 'High rcvr' },
                { bg: 'bg-teal-50',      text: 'text-teal-500',     label: 'Low rcvr' },
              ].map(({ bg, text, label }) => (
                <span key={label} className="flex items-center gap-1.5">
                  <span className={`w-3 h-3 rounded ${bg}`} />
                  <span className="text-xs text-gray-500">{label}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Top Bilateral Pairs */}
      {bilateral?.top_pairs?.length > 0 && (
        <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="top-bilateral-pairs">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Largest Bilateral Obligations</h2>
            <p className="text-xs text-gray-500 mt-0.5">Ranked by net settlement amount after multilateral netting</p>
          </div>
          <div className="divide-y divide-gray-100">
            {bilateral.top_pairs.slice(0, 8).map((pair) => (
              <div key={`${pair.payer}-${pair.receiver}`} className="flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50" data-testid={`pair-row-${pair.payer}-${pair.receiver}`}>
                <span className="w-5 text-xs font-mono text-gray-400 text-right flex-shrink-0">#{bilateral.top_pairs.indexOf(pair) + 1}</span>
                <span className="text-xs font-black font-mono text-red-700 flex-shrink-0">{pair.payer}</span>
                <ArrowRight size={11} weight="bold" className="text-gray-400 flex-shrink-0" />
                <span className="text-xs font-black font-mono text-emerald-700 flex-shrink-0">{pair.receiver}</span>
                <span className="flex-1" />
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-black font-mono text-gray-900">{fmt(pair.net_amount)}</p>
                  <p className="text-xs font-mono text-gray-400">{fmtCompact(pair.gross_payer_to_receiver)} / {fmtCompact(pair.gross_receiver_to_payer)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

    </div>
  );
};
